import { z } from 'zod';
import { centAmount, isoDate, uuid } from './posting.js';
import { unitPriceAmount } from './documents.js';
import { kundenportalEntscheidungArtSchema, kundenportalEreignisArtSchema, } from './portal-arten.js';
/**
 * Die Verträge des Kundenportals — Task 1 `[KP-grund]` des Plans
 * `docs/superpowers/plans/2026-09-16-kundenportal-api-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-16-kundenportal-api-design.md`, Abschnitte
 * 4, 5 und 6.
 *
 * **Diese Datei und Abschnitt 6 des Briefs für das Portal-Repository
 * (`claude-design-concept/fameaccounting-customerpanel/BRIEF-neues-chatfenster.md`)
 * beschreiben dieselben Routen.** Wer hier ein Feld ändert, ändert es dort im
 * selben Commit — das Portal wird gegen diesen Vertrag gebaut, in einem
 * anderen Repository, und hat keine zweite Quelle.
 *
 * **Kein Geldbetrag als `number`** (Hausregel 1): `centAmount` liefert
 * `Money`. Das gilt auch für die Felder, die das Portal nur anzeigt —
 * `bezahlt`, `offen`, `betrag` eines Verlaufseintrags —, denn ein Betrag, der
 * einmal durch eine `number` gelaufen ist, hat seine Genauigkeit schon
 * verloren, bevor jemand ihn addiert.
 *
 * **Warum so viele Felder `optional` und nicht `nullable` sind**, anders als
 * im Belegvertrag: Der Portal-Beleg ist keine Abbildung einer Tabellenzeile,
 * sondern eine ANSICHT für einen Endkunden. `faelligAm` an einem Angebot ist
 * nicht „null", es ist an einem Angebot keine Frage — dort heisst dasselbe
 * Datum `gueltigBis` (`D-4`). Ein `null` hiesse „wir wissen es nicht"; das
 * Weglassen heisst „diese Belegart kennt das Feld nicht". Die Ausnahme sind
 * die Felder der Betriebsliste am Ende dieser Datei: Dort ist `null` die
 * richtige Antwort, weil ein nicht erledigter Vorgang sehr wohl die Frage
 * „wann erledigt" kennt.
 *
 * **Der Token steht in KEINEM Schema dieser Datei ausser `portalBelegSchema`
 * und den Belegzeilen des Kundenkontos** — er ist ein Zugriffsschlüssel, und
 * jede Liste, jeder Suchtreffer, jeder Webhook-Rumpf, der ihn trüge, wäre
 * eine Kopie dieses Schlüssels an einem Ort, der nicht dafür gebaut ist
 * (`E-1410`).
 */
// ---------------------------------------------------------------------------
// Aufzählungen
// ---------------------------------------------------------------------------
/**
 * Die vier Belegarten, die das Portal kennt (`D-15`).
 *
 * Vierzehn `document_type`-Werte werden darauf abgebildet, vier weitere
 * (`order_confirmation`, `proforma`, `delivery_note`, `dunning`) antworten
 * `404` — die Abbildung selbst steht in `apps/api/src/portal/abbildung.ts`
 * (Task 2) und nicht hier: Sie ist eine Entscheidung des Dienstes, dieser
 * Vertrag sagt nur, was herauskommt.
 */
export const portalBelegArtSchema = z.enum(['rechnung', 'gutschrift', 'storno', 'angebot']);
/**
 * Der Zustand, den der Kunde sieht (Spec 6.2).
 *
 * Neun Werte für drei Welten: die Rechnung (`offen`, `teilbezahlt`,
 * `bezahlt`, `storniert`), Gutschrift und Storno (`ausgestellt`,
 * `storniert`) und das Angebot (die vier `angebot_*`). Sie stehen in EINER
 * Aufzählung, weil das Portal genau ein Feld anzeigt — und nicht in drei,
 * weil eine Anzeige, die erst die Art prüfen müsste, um das Statusfeld zu
 * verstehen, zwei Fehlerquellen hätte statt einer.
 */
export const portalBelegStatusSchema = z.enum([
    'offen',
    'teilbezahlt',
    'bezahlt',
    'storniert',
    'ausgestellt',
    'angebot_offen',
    'angebot_angenommen',
    'angebot_abgelehnt',
    'angebot_abgelaufen',
]);
/**
 * Der Steuerhinweis, den der Kunde auf dem Beleg lesen soll (`D-14`).
 *
 * Vier Werte aus sechs `tax_scenario`-Werten: `domestic` und
 * `eu_b2c_domestic_vat` bekommen KEINEN Hinweis — deutsche Umsatzsteuer
 * braucht keine Erklärung, und ein Hinweis, der an jedem zweiten Beleg steht,
 * wird nicht gelesen.
 */
export const portalSteuerhinweisSchema = z.enum([
    'reverse_charge',
    'nicht_steuerbar',
    'drittland',
    'oss',
]);
/**
 * Der eine Hinweis, den ein Altbeleg tragen kann (`KP-F5`).
 *
 * Drei Altbelege tragen `legacy_status = -1` („Fehler"), und die Werte `0`
 * und `3` sind zugelassen, aber nie gemessen. Sie erscheinen als `offen` mit
 * diesem Hinweis, statt als etwas ausgegeben zu werden, was sie vielleicht
 * nicht sind.
 */
export const portalBelegHinweisSchema = z.enum(['altbestand_unklar']);
/** `item` → `position`, `text` → `text` (`document_line_type`, `0019`). */
export const portalPositionArtSchema = z.enum(['position', 'text']);
/**
 * Die Arten des Verlaufs am Beleg (`D-6`).
 *
 * **Sieben davon sind ABGELEITET und stehen in keiner Tabelle**:
 * `ausgestellt` (`issue_date`), `versendet` (`document_versand`), `zahlung`
 * (freigegebene, nicht ersetzte Zuordnungen und Guthaben-Einlösungen),
 * `mahnung`, `storniert`, `angenommen`/`abgelehnt` (`angebot_entscheidung`).
 * Nur `geoeffnet` und `pdf_geladen` werden gespeichert (`portal_ereignis`) —
 * sie sind die einzigen, die sonst niemand wüsste.
 */
export const portalEreignisArtSchema = z.enum([
    'ausgestellt',
    'versendet',
    'geoeffnet',
    'pdf_geladen',
    'zahlung',
    'mahnung',
    'storniert',
    'angenommen',
    'abgelehnt',
]);
/**
 * Die zwei Arten, die das PORTAL melden darf.
 *
 * Eine echte Teilmenge von {@link portalEreignisArtSchema} und bewusst eine
 * eigene Aufzählung: Ein Portal, das `bezahlt` melden dürfte, könnte einen
 * Zahlungsverlauf behaupten. Es meldet, was nur es weiss, und nichts sonst.
 *
 * **Aus `./portal-arten.js` importiert** — seit Befund H8 des Reviews vom
 * 2026-09-17 (`KP-oberflaeche`) dieselbe Objektreferenz wie
 * `dokumentPortalEreignisArtSchema` in `documents.ts`. Der öffentliche Name
 * bleibt `portalKundenereignisArtSchema`, damit `KP-beleg`/`KP-angebot`/
 * `KP-kunde` beim Stapeln nichts umbenennen müssen.
 */
export const portalKundenereignisArtSchema = kundenportalEreignisArtSchema;
/**
 * Angenommen oder abgelehnt — es gibt keinen dritten Zustand (`D-3`).
 *
 * **Aus `./portal-arten.js` importiert**, aus demselben Grund und seit
 * demselben Befund (H8) wie {@link portalKundenereignisArtSchema} — dieselbe
 * Objektreferenz wie das `art`-Feld von `dokumentAngebotEntscheidungSchema`
 * in `documents.ts`.
 */
export const portalEntscheidungArtSchema = kundenportalEntscheidungArtSchema;
/** Rückfrage zum Beleg oder Antrag auf Änderung der Rechnungsdaten (`D-5`). */
export const portalVorgangArtSchema = z.enum(['rueckfrage', 'aenderungsantrag']);
// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------
/**
 * Die Obergrenze jedes freien Textfeldes dieser Ära, in Zeichen.
 *
 * **Dieselbe Zahl wie die CHECK-Bedingungen `angebot_entscheidung_laengen`
 * (`0235`: `agb_fassung`, `bestellnummer`, `anmerkung`, `grund`) und
 * `portal_vorgang_notiz_laenge` (`0236`: `erledigt_notiz`)**, und sie steht
 * hier genau einmal. Berichtigt am 2026-09-25 (`KP-eingabehaertung`): Bis
 * dahin stand hier, die Zahl gelte als CHECK in `0235` UND `0236` für jedes
 * freie Textfeld — für `0236` war das falsch. `portal_vorgang.inhalt` ist
 * `jsonb` und trug dort nur den Schlüssel-CHECK; eine Obergrenze bekommt die
 * Spalte erst mit `0242`, und zwar als GRÖSSE (`pg_column_size(inhalt) <=
 * 16384`), weil eine jsonb-Spalte keine Zeichenzahl je Feld kennt. Die
 * Feldgrenzen des Inhalts hält der Vertrag allein.
 *
 * `ip` und `user_agent` tragen eigene, engere Grenzen — im Vertrag seit
 * `KP-grund`, in der Datenbank seit `0242` (`*_herkunft_laenge` auf
 * `portal_vorgang`, `portal_ereignis`, `angebot_entscheidung`). Dieselben
 * Zahlen an beiden Orten: {@link PORTAL_IP_MAX}, {@link PORTAL_USER_AGENT_MAX}.
 */
export const PORTAL_TEXT_MAX = 2000;
/**
 * **Jede Längengrenze dieser Datei zählt Unicode-CODEPUNKTE** (`[...s].length`)
 * und keine UTF-16-Einheiten — seit `KP-eingabehaertung` (2026-09-25), abgestimmt
 * mit der Prüfliste des Portals. Ein Emoji ist EIN Zeichen, wie JSON Schema
 * `maxLength` definiert und wie das Portal zählt. Zods eigenes `.max()` zählt
 * UTF-16-Einheiten; eine Rückfrage aus 1001 bis 2000 Emoji bestünde das Portal
 * und fiele hier durch. Deshalb steht keine dieser Zahlen in einem `.max()`
 * eines Texts, sondern in {@link codepunkte}.
 */
/** `ip` — Vertrag und `0242` (`char_length(ip) <= 64`). */
export const PORTAL_IP_MAX = 64;
/** `userAgent` — Vertrag und `0242` (`char_length(user_agent) <= 512`). */
export const PORTAL_USER_AGENT_MAX = 512;
/** Eine E-Mail-Adresse — RFC 5321, Pfad höchstens 254 Zeichen. */
export const PORTAL_EMAIL_MAX = 254;
/** Die Kennung der Kontoauflösung — E-Mail oder Anzeige-Kundennummer (`D-10`), 3 bis 254. */
export const PORTAL_KENNUNG_MIN = 3;
/** Siehe {@link PORTAL_KENNUNG_MIN}. */
export const PORTAL_KENNUNG_MAX = 254;
/** Die Belegnummer eines Altlinks. */
export const PORTAL_NUMMER_MAX = 32;
/** Der Hash eines Altlinks (`h`). */
export const PORTAL_HASH_MAX = 128;
/** Name und jede einzelne Anschriftszeile eines Änderungsantrags. */
export const PORTAL_NAME_MAX = 200;
/** Die Zahl der Zeilen einer beantragten Anschrift. */
export const PORTAL_ANSCHRIFT_ZEILEN_MAX = 6;
/** Eine Telefonnummer — Ziffern, Leerzeichen, `+ - / ( )`, mindestens eine Ziffer. */
export const PORTAL_TELEFON_MAX = 40;
/** Eine USt-IdNr., roh — mit Trennern (Leerzeichen, Punkt, Bindestrich) höchstens 20. */
export const PORTAL_UST_ID_MAX = 20;
/** Die Bestellnummer einer Angebotsannahme. */
export const PORTAL_BESTELLNUMMER_MAX = 100;
/** Ein ISO-Zeitpunkt mit Zone — `2026-09-25T10:00:00.123456789+02:00` hat 35 Zeichen. */
export const PORTAL_ZEITPUNKT_MAX = 40;
// ---------------------------------------------------------------------------
// Mengengrenzen — seit `KP-mengengrenzen` (2026-09-26, `D-28`)
// ---------------------------------------------------------------------------
//
// Die Längengrenzen oben begrenzen EINE Eingabe; diese hier begrenzen, WIE
// VIELE. Das Portal drosselt je Sitzung (fünf Anträge je Stunde und Konto) —
// das ist das erste Netz. Diese Zahlen sind das zweite, und sie gelten auch
// dann, wenn der Bearer des Portals in fremde Hände gerät: Wer ihn hat, ruft
// die API ohne Portal und ohne dessen Sitzungsgrenze.
//
// Hier stehen die Grenzen, die ein Aufrufer an einer ANTWORT sieht. Die zwei,
// die nur die API kennt — das Dublettenfenster und die Drossel je Token —,
// stehen in `apps/api/src/portal/mengengrenzen.ts`.
/**
 * Höchstens so viele OFFENE Vorgänge (Rückfragen und Änderungsanträge
 * zusammen) je Kunde. Der elfte antwortet `409 PORTAL_VORGANG_LIMIT`, und die
 * Zahl steht als `hoechstens` neben dem Code — die Meldung selbst nennt sie
 * nicht. Ein erledigter Vorgang zählt nicht mehr.
 */
export const PORTAL_VORGAENGE_OFFEN_MAX = 10;
/**
 * Höchstens so viele Einträge im Verlauf eines Belegs (`ereignisse`), die
 * JÜNGSTEN; `ereignisseGesamt` nennt, wie viele es insgesamt sind. Dieselbe
 * Zahl wie die Belegseite der Sachbearbeitung.
 */
export const PORTAL_EREIGNISSE_MAX = 50;
/** Die Seitenlänge der Vorgangsliste der Sachbearbeitung, wenn keine angegeben ist. */
export const PORTAL_VORGAENGE_SEITE_VORGABE = 50;
/** Die grösste Seitenlänge, die die Vorgangsliste annimmt. */
export const PORTAL_VORGAENGE_SEITE_MAX = 200;
// ---------------------------------------------------------------------------
// Der Texttyp (`KP-eingabehaertung`, 2026-09-25)
// ---------------------------------------------------------------------------
/**
 * Formatzeichen (`\p{Cf}`): Nullbreitenzeichen, weicher Trennstrich, BOM,
 * Richtungsmarken und -steuerzeichen, Tag-Zeichen `U+E0000`–`U+E007F` und ihre
 * Geschwister.
 *
 * Eine Unicode-Eigenschaftsklasse und keine Aufzählung: Die Portal-Prüfliste
 * weist JEDES Formatzeichen ab, und eine handgeschriebene Liste bliebe hinter
 * der Eigenschaft zurück, sobald Unicode eines hinzufügt. Kein `/g`, kein `/y`
 * — der Ausdruck trägt keinen Zustand (`pnpm regex:zustand`).
 */
const FORMATZEICHEN = /^\p{Cf}$/u;
/** `U+200C` ZWNJ und `U+200D` ZWJ — in Namen, Anschriften und Freitexten erlaubt. */
function istVerbinder(cp) {
    return cp === 0x200c || cp === 0x200d;
}
/**
 * Das erste unzulässige Zeichen eines Textes, als Codepunkt, oder `undefined`.
 *
 * **Unzulässig** sind, ohne Rücksicht auf ihre Stellung im Text:
 *
 * - die Steuerzeichen C0 (`U+0000`–`U+001F`, auch NUL, Tabulator und
 *   Wagenrücklauf) und C1 (`U+007F`–`U+009F`) — **ausser** dem Zeilenumbruch
 *   `U+000A`, und den nur in einem MEHRZEILIGEN Feld;
 * - Zeilen- und Absatztrenner (`U+2028`, `U+2029`);
 * - jedes Formatzeichen (`\p{Cf}`) — darunter die Richtungssteuerzeichen
 *   (`U+061C`, `U+200E`, `U+200F`, `U+202A`–`U+202E`, `U+2066`–`U+2069`), die
 *   einen Text anders aussehen lassen, als er gelesen wird („Trojan Source"),
 *   das Nullbreitenzeichen `U+200B`, der weiche Trennstrich `U+00AD`, das BOM
 *   `U+FEFF` und die Tag-Zeichen `U+E0000`–`U+E007F` — **ausser** den zwei
 *   Verbindern `U+200C`/`U+200D`, und die nur, wo `verbinder` es erlaubt
 *   (Name, Anschriftszeile, Freitext: Schriften wie Persisch und Emoji-Folgen
 *   brauchen sie; eine Kennung, eine Belegnummer oder ein Hash nicht);
 * - einzelne Surrogate (`U+D800`–`U+DFFF` ohne Partner) — JSON lässt sie zu,
 *   UTF-8 kann sie nicht darstellen.
 *
 * **Warum eine Schleife über Codepunkte und kein regulärer Ausdruck für die
 * Steuerzeichen:** Ein Muster mit `\u0000` trifft die ESLint-Regel
 * `no-control-regex`, und eine Ausnahme davon an einer Stelle, deren ganzer
 * Zweck Steuerzeichen sind, läse sich wie ein Versehen. `for … of` liefert
 * Codepunkte, einzelne Surrogate als eigenen Eintrag — genau die Einheit, die
 * hier zählt.
 */
export function unzulaessigesPortalZeichen(wert, mehrzeilig, verbinder = false) {
    for (const zeichen of wert) {
        const cp = zeichen.codePointAt(0) ?? 0;
        if (cp === 0x0a && mehrzeilig)
            continue;
        if (cp <= 0x1f || (cp >= 0x7f && cp <= 0x9f))
            return cp;
        if (cp === 0x2028 || cp === 0x2029)
            return cp;
        if (cp >= 0xd800 && cp <= 0xdfff)
            return cp;
        if (FORMATZEICHEN.test(zeichen) && !(verbinder && istVerbinder(cp)))
            return cp;
    }
    return undefined;
}
/** `U+202E` — die Schreibweise, in der eine Ablehnung das Zeichen nennt. */
function codepunkt(cp) {
    return `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
}
/** Die Zahl der Codepunkte eines Textes — die Einheit jeder Grenze dieser Datei. */
export function codepunktLaenge(wert) {
    return [...wert].length;
}
/**
 * Eine Längengrenze in CODEPUNKTEN, als Zod-Prüfung, die der OpenAPI-Generator
 * als `minLength`/`maxLength` liest.
 *
 * Ein regulärer Ausdruck `^[\s\S]{min,max}$` mit `u`-Schalter zählt
 * Codepunkte (ein Surrogatpaar ist EIN `[\s\S]`), und `zeichenkette()` in
 * `apps/api/src/openapi/json-schema.ts` erkennt genau diese Form und schreibt
 * sie als `minLength`/`maxLength` — die JSON-Schema-Schlüsselwörter zählen
 * ebenfalls Codepunkte. Ein `.max()` hätte die Beschreibung geliefert und die
 * falsche Einheit geprüft.
 *
 * Neu gebaut je Aufruf und ohne `/g`: Das Objekt trägt keinen Zustand.
 */
export function codepunkte(min, max) {
    return new RegExp(`^[\\s\\S]{${String(min)},${String(max)}}$`, 'u');
}
/**
 * Die Zeichenprüfung und die Länge am kanonischen Wert, als Zod-Verfeinerung.
 *
 * **Die Meldung nennt das ZEICHEN und nie den TEXT.** Eine Ablehnung wandert
 * in die Antwort an das Portal, in dessen Protokoll und in Bildschirmfotos;
 * eine Rückfrage kann eine Anschrift, eine Telefonnummer, eine Klage tragen.
 * Der Codepunkt ist keine Auskunft über den Kunden, der Text wäre eine.
 */
function kanonischePruefung(optionen) {
    return (wert, ctx) => {
        const laenge = codepunktLaenge(wert);
        if (laenge < optionen.min) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: optionen.min === 1
                    ? 'Darf nicht leer sein'
                    : `Mindestens ${String(optionen.min)} Zeichen`,
            });
            return;
        }
        if (laenge > optionen.max) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Höchstens ${String(optionen.max)} Zeichen`,
            });
            return;
        }
        const cp = unzulaessigesPortalZeichen(wert, optionen.mehrzeilig, optionen.verbinder);
        if (cp === undefined)
            return;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Enthält ein unzulässiges Steuer-, Trenn- oder Formatzeichen (${codepunkt(cp)})` +
                (optionen.mehrzeilig ? '; erlaubt ist nur der Zeilenumbruch' : ''),
        });
    };
}
/**
 * Die Kanonisierung eines Portaltextes: Ränder, NFC — und sonst nichts.
 *
 * - **An beiden Enden fallen Leerzeichen (`U+0020`) und Zeilenumbrüche
 *   (`U+000A`), und nur diese.** Ausdrücklich NICHT `String.prototype.trim()`:
 *   Das nähme auch Tabulator, Wagenrücklauf, Zeilentrenner und BOM am Rand
 *   still weg — ein Zeichen, das im Inneren abgewiesen würde, verschwände am
 *   Rand ungesehen. Die Portal-Prüfliste verlangt dasselbe: Tabulator, CR,
 *   Trenner oder BOM am Rand sind ein Verstoss und keine Reparatur.
 * - **NFC** — „ö" als EIN Zeichen und nicht als „o" plus Trema. Beide sehen
 *   gleich aus, vergleichen aber ungleich, zählen verschieden lang und
 *   durchsuchen sich verschieden; die Datenbank soll eine Form sehen.
 *
 * **Kein Umbau von `\r\n` zu `\n`:** Das Portal schickt nie ein CR, und ein
 * CR, das trotzdem kommt, wird abgewiesen statt umgebaut — „abweisen, nie
 * reparieren", dieselbe Haltung wie auf der Portalseite.
 */
function kanonisiere(roh) {
    let anfang = 0;
    let ende = roh.length;
    while (anfang < ende && (roh.charCodeAt(anfang) === 0x20 || roh.charCodeAt(anfang) === 0x0a)) {
        anfang += 1;
    }
    while (ende > anfang && (roh.charCodeAt(ende - 1) === 0x20 || roh.charCodeAt(ende - 1) === 0x0a)) {
        ende -= 1;
    }
    return roh.slice(anfang, ende).normalize('NFC');
}
/**
 * **Der Texttyp jeder freien Portal-Eingabe** (`KP-eingabehaertung`).
 *
 * Drei Schritte, in dieser Reihenfolge:
 *
 * 1. **Die rohe Länge in Codepunkten** ({@link codepunkte}) — vor jeder
 *    Umformung, damit eine übergrosse Eingabe gar nicht erst normalisiert wird,
 *    und damit die OpenAPI-Beschreibung die Grenze trägt: Der Generator liest
 *    die EINGABESEITE eines Schemas (`apps/api/src/openapi/json-schema.ts`).
 * 2. **Die Kanonisierung** ({@link kanonisiere}): Ränder aus Leerzeichen und
 *    Zeilenumbrüchen, NFC.
 * 3. **Länge und Zeichen am kanonischen Wert** — `min` hier, denn „drei
 *    Leerzeichen" ist leer, und NFC kann die Zahl der Codepunkte ändern.
 *
 * **Die Kanonisierung ist nur echt, wenn ein Dienst `parse()` fährt**
 * (Handbuch 3.714, `pnpm rumpf:kanon`): Ein Controller, der die Eingabe mit
 * `pruefeRumpf()` prüft und die UNGEPRÜFTE weiterreicht, hat den Text nie
 * beschnitten. Jede Portalroute tut deshalb eines von beidem — `parse()` im
 * Controller, oder `pruefeRumpf()` im Controller und `parse()` im Dienst.
 */
export function portalText(optionen) {
    const voll = {
        max: optionen.max,
        min: optionen.min ?? 0,
        mehrzeilig: optionen.mehrzeilig === true,
        verbinder: optionen.verbinder === true,
    };
    return z
        .string()
        .regex(codepunkte(voll.min, voll.max), `${String(voll.min)} bis ${String(voll.max)} Zeichen`)
        .transform(kanonisiere)
        .pipe(z.string().superRefine(kanonischePruefung(voll)));
}
/** Ein einzeiliger Kennzeichentext — Kennung, Belegnummer, Hash, Bestellnummer, AGB-Fassung. */
export function portalZeile(max, min = 0) {
    return portalText({ max, min });
}
/** Eine einzeilige Namensangabe — Name, Anschriftszeile: Verbinder erlaubt. */
export function portalNamenszeile(max, min = 0) {
    return portalText({ max, min, verbinder: true });
}
/** Ein mehrzeiliger Portaltext — Rückfrage, Anmerkung, Grund, Notiz: Umbruch und Verbinder erlaubt. */
export function portalFreitext(max = PORTAL_TEXT_MAX, min = 0) {
    return portalText({ max, min, mehrzeilig: true, verbinder: true });
}
/**
 * Eine E-Mail-Adresse aus dem Portal: beschnitten wie jeder Portaltext, dann
 * Zods `email()` (nur ASCII, kein Leerzeichen, keine Klammer, kein
 * Anführungszeichen) — dieselbe Prüfung, die das Portal fährt.
 */
const portalEmail = portalZeile(PORTAL_EMAIL_MAX, 1).pipe(z.string().email('Keine gültige E-Mail-Adresse'));
/**
 * Eine Telefonnummer: Ziffern, Leerzeichen, `+ - / ( )`, mindestens eine
 * Ziffer, 1 bis 40 Zeichen — genau die Klasse der Portal-Prüfliste.
 */
const portalTelefon = portalZeile(PORTAL_TELEFON_MAX, 1).pipe(z
    .string()
    .regex(/^[0-9 +\-/()]+$/u, 'Nur Ziffern, Leerzeichen und + - / ( )')
    .regex(/[0-9]/u, 'Mindestens eine Ziffer'));
/**
 * Eine USt-IdNr.: roh mit Trennern (Leerzeichen, Punkt, Bindestrich) und in
 * beliebiger Schreibung, bis 20 Zeichen — so, wie das Portal sie als
 * Platzhalter zeigt („DE 000 000 000", „CHE-123.456.789"). Gespeichert wird die
 * NORMALISIERTE Form: Trenner entfernt, grossgeschrieben, danach geprüft gegen
 * `^[A-Z]{2}[A-Z0-9]{2,13}$`. Ein `transform` — er wirkt, weil der Dienst
 * `parse()` fährt (`pnpm rumpf:kanon`).
 */
const portalUstId = portalZeile(PORTAL_UST_ID_MAX, 1)
    .transform((wert) => wert.replaceAll(/[ .-]/gu, '').toUpperCase())
    .pipe(z
    .string()
    .regex(/^[A-Z]{2}[A-Z0-9]{2,13}$/u, 'USt-IdNr. als Länderkennzeichen und 2 bis 13 Stellen'));
/**
 * Eine Herkunftsangabe (`ip`, `userAgent`) — begrenzt, nicht leer, ohne
 * Steuer- und Formatzeichen, und **nicht umgeformt**: `D-8` sagt „gespeichert,
 * wie geliefert", und ein beschnittener User-Agent wäre nicht mehr der
 * gelieferte.
 */
function herkunftsangabe(max) {
    return z
        .string()
        .regex(codepunkte(1, max), `1 bis ${String(max)} Zeichen`)
        .superRefine(kanonischePruefung({ max, min: 1, mehrzeilig: false, verbinder: false }));
}
/**
 * Eine Anschrift als fertige Zeilen.
 *
 * Nicht als Feldsatz (`strasse`, `plz`, `ort`, …): Der Beleg friert die
 * Anschrift des Kunden beim Ausstellen ein, und wie sie gegliedert war, ist
 * eine Frage des Ausstellungstages und nicht der Anzeige. `anschriftZeilen()`
 * im Dienst (Task 2) baut sie für Kunde und Aussteller aus DERSELBEN
 * Funktion; leere Glieder fallen dabei weg.
 */
const anschrift = z.array(z.string());
/** Eine Zeitangabe mit Zone, ISO-8601 — wie jeder Zeitstempel dieser API (AUSGABE). */
const zeitpunkt = z.string();
/**
 * Ein Zeitpunkt, den das PORTAL meldet — ISO-8601 **mit** Zone.
 *
 * Bis 2026-09-25 ein blankes `z.string()`, und der Wert ging als
 * `timestamptz`-Parameter in die Datenbank: Unlesbares wurde eine `500`, und
 * `infinity` nahm PostgreSQL an — `to_char` liefert dafür `NULL`, und die
 * Belegansicht dieses Kunden starb an `localeCompare` (`E-1470`). Das Muster
 * hier schliesst `infinity`, `epoch`, ein blosses Datum und einen Zeitpunkt
 * ohne Zone aus; das FENSTER (nicht vor dem Belegdatum, höchstens fünf Minuten
 * in der Zukunft) prüft der Dienst gegen den Beleg, weil nur er das Belegdatum
 * kennt (`apps/api/src/portal/zeitfenster.ts`, `E-1469`).
 */
const zeitpunktEingabe = z
    .string()
    .max(PORTAL_ZEITPUNKT_MAX)
    .datetime({ offset: true, message: 'Zeitpunkt als ISO-8601 mit Zone erwartet' });
// ---------------------------------------------------------------------------
// `GET /portal/belege/:token` — die Antwort
// ---------------------------------------------------------------------------
/**
 * Eine Belegposition, wie das Portal sie zeigt (Spec 6.1).
 *
 * **Die sieben Wertfelder sind optional, und das ist keine Bequemlichkeit.**
 * Eine TEXTZEILE (`document_line_text`, `0022`) trägt Menge, Einheit, Preis
 * und Satz als NULL — der Altimport hängt an jede Gutschrift eine —, und ein
 * Vertrag, der dort `"0.00"` verlangte, zwänge den Dienst, eine Zahl zu
 * erfinden, die auf keinem PDF steht. Sie fehlen bei `art: 'text'` und stehen
 * bei `art: 'position'`.
 *
 * `text` kommt **wie gespeichert** aus `description` und trägt die
 * Sechser-Whitelist aus `POSITIONSTEXT_TAGS_LESEN`
 * (`packages/contracts/src/positionstext.ts`, CHECK in `0154`): `br`, `b`,
 * `i`, `ul`, `li`, `p`. Das Portal reinigt mit derselben Sechserliste noch
 * einmal — nicht, weil hier etwas durchkäme, sondern weil ein Anzeiger, der
 * fremdem HTML vertraut, beim ersten Fehler in der Kette eine Lücke ist.
 */
export const portalPositionSchema = z
    .object({
    pos: z.number().int().positive(),
    art: portalPositionArtSchema,
    text: z.string(),
    menge: z.string().optional(),
    einheit: z.string().optional(),
    /** `unit_price` — bis zu vier Nachkommastellen, dieselbe Form wie im Belegvertrag. */
    einzelpreis: unitPriceAmount.optional(),
    /**
     * Der Steuersatz als PROZENTWERT mit zwei Nachkommastellen (`'19.00'`, `'7.00'`,
     * `'0.00'`) — gerechnet aus dem gespeicherten Faktor `tax_rate` (`0.19`). Das Portal
     * zeigt ihn, wie er auf der Rechnung steht; ein Faktor hiesse dort 0,19 %.
     */
    steuersatz: z.string().regex(/^\d{1,2}\.\d{2}$/u).optional(),
    netto: centAmount.optional(),
    steuer: centAmount.optional(),
    /** Der Bruttobetrag der Zeile (`gross_amount`). */
    betrag: centAmount.optional(),
})
    .strict();
/**
 * Der Rechnungsempfänger, wie er beim Ausstellen eingefroren wurde.
 *
 * Die `billing_*`-Spalten des BELEGS und nicht der heutige Stand des Kunden:
 * Wer seine Anschrift nach der Rechnung ändert, ändert nicht die Rechnung.
 */
export const portalKundenkopfSchema = z
    .object({
    kundennummer: z.string(),
    name: z.string(),
    anschrift,
    ustId: z.string().optional(),
    /**
     * `document.billing_salutation` (Auftrag `KP-leistungszeitraum`) — die
     * Anrede DES BELEGS und nicht die heutige des Kundenstamms, dieselbe
     * Eingefroren-Begründung wie beim Rest dieses Objekts. Für Angebote gilt
     * dieselbe Spalte.
     *
     * `nullable()` und nicht nur `optional()`, anders als `ustId` oben: Nach
     * der Regel am Dateikopf dieser Datei ist `null` hier die richtige
     * Antwort — der Beleg KENNT das Feld (jeder Rechnungsempfänger hat eine
     * Anrede-Frage), sie ist an dieser Zeile nur nicht gesetzt gewesen
     * (`billing_salutation` ist in der Datenbank nullbar). `optional()` bleibt
     * zusätzlich stehen, weil das Feld additiv ist und ein älterer, gegen
     * diesen Vertrag geparster Rumpf ohne das Feld nicht bricht.
     */
    anrede: z.string().nullable().optional(),
})
    .strict();
/** Der Aussteller aus `issuer_profile` des Belegs. */
export const portalAusstellerSchema = z
    .object({
    name: z.string(),
    anschrift,
    email: z.string().optional(),
    telefon: z.string().optional(),
    ustId: z.string().optional(),
    steuernummer: z.string().optional(),
    handelsregister: z.string().optional(),
    geschaeftsfuehrung: z.string().optional(),
})
    .strict();
/**
 * Die Überweisungsdaten (Spec 6.1).
 *
 * **Nur an einer Rechnung, und nur, solange sie nicht storniert ist** — auf
 * einen Storno zahlt niemand, und dieselbe Grenze zieht der Rechnungsdruck.
 * `bic` und `bank` sind in `issuer_bank_account` (`0021`) nullbar: SEPA im
 * Inland kommt seit 2016 ohne BIC aus.
 *
 * `epcQr` ist der FERTIGE EPC-QR-Text und kein Bild: Das Portal zeichnet den
 * Code, fameaccounting sagt, was drinsteht. Er fehlt, wenn der Beleg eine
 * Lastschriftankündigung trägt — wer eingezogen wird, soll nicht zusätzlich
 * überweisen.
 */
export const portalZahlungSchema = z
    .object({
    kontoinhaber: z.string(),
    iban: z.string(),
    bic: z.string().optional(),
    bank: z.string().optional(),
    verwendungszweck: z.string(),
    epcQr: z.string().optional(),
})
    .strict();
/**
 * Die SEPA-Vorabankündigung am Beleg (`document.lastschrift_ankuendigung`,
 * `0232`).
 *
 * Hier MIT Gläubiger-ID, im Kundenkonto ohne: Sie steht am Ausstellerprofil
 * und gehört auf den Beleg, den sie betrifft — ein Kundenkonto hat kein
 * Profil (`E-1405`).
 */
export const portalLastschriftSchema = z
    .object({
    mandatsreferenz: z.string(),
    glaeubigerId: z.string(),
    ibanMaskiert: z.string(),
    einzugAm: isoDate,
})
    .strict();
/** Die Mahndaten des Altsystems (`legacy_dunning_*`, `0158`), falls Stufe > 0. */
export const portalMahnungSchema = z
    .object({
    stufe: z.number().int().positive(),
    gebuehr: centAmount,
    zinsen: centAmount,
    /** Der Verzugszinssatz in Prozent, als Text — `'4.12'` oder `'8.12'`. */
    zinssatz: z.string(),
    /** Der Tag, auf den `zinsen` gerechnet ist. */
    stand: isoDate,
})
    .strict();
/**
 * Ein Eintrag des Verlaufs (`E-1400`).
 *
 * `betrag` steht nur an `zahlung` — und dort nur für die HAUPTFORDERUNG
 * (`zahlung_zuordnung_position.art = 'hauptforderung'`): Mahnkosten und
 * Zinsen sind keine Tilgung (`D-4` der Zahlungszuordnungs-Spec), und ein
 * Verlauf, der sie mitzählte, zeigte dem Kunden eine Tilgung, die es nicht
 * gab.
 */
export const portalEreignisSchema = z
    .object({
    art: portalEreignisArtSchema,
    zeitpunkt,
    betrag: centAmount.optional(),
})
    .strict();
/** Die Entscheidung am Angebot, wie der Kunde sie wiedersieht. */
export const portalEntscheidungSchema = z
    .object({
    art: portalEntscheidungArtSchema,
    zeitpunkt,
    bestellnummer: z.string().optional(),
})
    .strict();
/**
 * Der Bezug auf einen anderen Beleg (`E-1401`).
 *
 * MIT Token: Der Kunde soll vom Storno zur Rechnung springen können, ohne
 * einen zweiten Link zu suchen. Deshalb steht er hier — und deshalb steht er
 * nur dann, wenn der Zielbeleg selbst eine Portal-Art hat.
 */
export const portalBezugSchema = z
    .object({
    art: z.enum(['rechnung', 'angebot', 'gutschrift']),
    nummer: z.string(),
    token: z.string(),
})
    .strict();
/**
 * Der Leistungszeitraum am Belegkopf (Auftrag `KP-leistungszeitraum`).
 *
 * **Es gibt keine Spalte `document.service_period_start/_end`** — der
 * Leistungszeitraum ist ein Feld der POSITION (`document_line.service_
 * period_start/_end`, `0022`), nicht des Belegkopfs; Migration `0122`
 * begründet das ausdrücklich damit, dass ein Beleg eine Hosting- und eine
 * Einmalposition zugleich tragen kann. Dieses Objekt ist deshalb eine
 * ABLEITUNG über alle Positionen und keine Spaltenabbildung.
 */
export const portalLeistungszeitraumSchema = z
    .object({
    von: isoDate,
    bis: isoDate,
})
    .strict();
/** Ein Beleg, wie das Portal ihn zeigt (Spec 6.1, Brief 6.1). */
export const portalBelegSchema = z
    .object({
    token: z.string(),
    art: portalBelegArtSchema,
    /** Aus dem Vorgängersystem importiert (`ist_archivbelegtyp`). */
    altbestand: z.boolean(),
    nummer: z.string(),
    /** Das Belegdatum (`issue_date`). */
    datum: isoDate,
    /**
     * Der Leistungszeitraum, EIN Wert für den ganzen Beleg (Auftrag
     * `KP-leistungszeitraum`) — abgeleitet aus `document_line.service_
     * period_start/_end` **aller** Positionen, nicht gespeichert.
     *
     * `null`, wenn KEINE Position einen vollständigen Zeitraum trägt ODER
     * wenn die Positionen UNEINHEITLICHE Zeiträume tragen (verschiedene
     * Paare `von`/`bis` an verschiedenen Positionen) — ein Beleg mit einer
     * Hosting- und einer Einmalposition hat header-seitig KEINEN einzelnen
     * Leistungszeitraum, und ein zusammengefasster Wert (kleinstes `von`,
     * grösstes `bis`) wäre eine erfundene Spanne, die an keiner Position
     * so steht. Eine Position, die nur EINE der beiden Spalten trägt, zählt
     * dabei als „kein Zeitraum" — dieselbe Regel wie am PDF
     * (`render.service.ts`: `servicePeriod: … === null || … === null ? null
     * : …`), „eine halbe Angabe ist keine Angabe".
     *
     * `nullable()` und nicht nur `optional()`: Nach der Regel am Dateikopf
     * ist das hier ein echtes „wir wissen es, und die Antwort ist keiner" —
     * jeder Beleg kennt die Frage nach einem Leistungszeitraum, sie ist nur
     * an manchen Belegen unbeantwortet oder uneindeutig. `optional()`
     * zusätzlich, weil das Feld additiv ist.
     */
    leistungszeitraum: portalLeistungszeitraumSchema.nullable().optional(),
    /** `due_date`, nur an einer Rechnung. */
    faelligAm: isoDate.optional(),
    /** `due_date`, nur an einem Angebot — dort ist es die Bindefrist (`D-4`). */
    gueltigBis: isoDate.optional(),
    status: portalBelegStatusSchema,
    hinweis: portalBelegHinweisSchema.optional(),
    /**
     * Die Währung des Belegs (`currency`).
     *
     * `z.string()` und nicht `z.literal('EUR')`, obwohl heute jeder Beleg auf
     * Euro lautet: Die Datenbank lässt eine andere zu, und ein Vertrag, der
     * mehr zusagte als das Schema durchsetzt, wäre eine erfundene
     * Zusicherung — dieselbe Erwägung wie bei `currency` im Belegvertrag.
     */
    waehrung: z.string(),
    netto: centAmount,
    steuer: centAmount,
    brutto: centAmount,
    /**
     * `bezahlt + offen = brutto` an jeder NICHT stornierten Rechnung; eine
     * stornierte trägt beide `"0.00"`, und ein Angebot ebenso. Mahnkosten und
     * Zinsen stehen unter `mahnung` NEBEN `offen` und nicht darin.
     */
    bezahlt: centAmount,
    offen: centAmount,
    steuerhinweis: portalSteuerhinweisSchema.optional(),
    mahnung: portalMahnungSchema.optional(),
    positionen: z.array(portalPositionSchema),
    kunde: portalKundenkopfSchema,
    aussteller: portalAusstellerSchema,
    zahlung: portalZahlungSchema.optional(),
    lastschrift: portalLastschriftSchema.optional(),
    pdfVerfuegbar: z.boolean(),
    /**
     * Chronologisch, älteste zuerst (`E-1400`) — und seit `KP-mengengrenzen`
     * höchstens die {@link PORTAL_EREIGNISSE_MAX} JÜNGSTEN (`E-1488`). Wer
     * mehr hat, sieht die letzten fünfzig in derselben Ordnung; der älteste
     * Eintrag der Liste ist dann nicht mehr «ausgestellt».
     */
    ereignisse: z.array(portalEreignisSchema),
    /**
     * Wie viele Verlaufseinträge der Beleg insgesamt hat — gleich
     * `ereignisse.length`, solange es höchstens {@link PORTAL_EREIGNISSE_MAX}
     * sind, sonst grösser. Das Portal zeigt daran, dass es ältere gibt.
     */
    ereignisseGesamt: z.number().int().nonnegative(),
    entscheidung: portalEntscheidungSchema.optional(),
    bezug: portalBezugSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Die schreibenden Rümpfe
// ---------------------------------------------------------------------------
/**
 * Herkunftsangaben, die JEDER schreibende Portal-Rumpf trägt (`D-8`).
 *
 * Sie werden gespeichert, wie geliefert, und **nie für Berechtigungen
 * benutzt**: Die Berechtigung hängt am Bearer-Token und an der Zeile in
 * `api_token_permission`, an nichts sonst. Die wahre Quelladresse jedes
 * Aufrufs steht ohnehin im Zugriffs-Log — diese hier ist die des KUNDEN, die
 * nur das Portal kennt.
 */
const herkunft = {
    // Pflicht und nicht leer: Der Brief (Abschnitt 6) nennt beide ohne Fragezeichen,
    // und eine Leerzeichenkette waere eine Herkunft, die keine ist.
    // Seit `KP-eingabehaertung` zusätzlich ohne Steuerzeichen und mit denselben
    // Grenzen als CHECK in `0242` — aber nicht umgeformt, siehe
    // `herkunftsangabe()`.
    ip: herkunftsangabe(PORTAL_IP_MAX),
    userAgent: herkunftsangabe(PORTAL_USER_AGENT_MAX),
};
/** `POST /portal/belege/:token/ereignisse` → `204`. */
export const portalEreignisEingabeSchema = z
    .object({
    art: portalKundenereignisArtSchema,
    zeitpunkt: zeitpunktEingabe,
    ...herkunft,
})
    .strict();
/**
 * `POST /portal/belege/:token/entscheidung` → `201`.
 *
 * Die Pflicht der AGB-Fassung bei der Annahme steht hier UND als CHECK in
 * `0235` (`angebot_entscheidung_agb_bei_annahme`). Das ist keine Dopplung
 * ohne Grund: Der Vertrag beantwortet sie mit einer lesbaren Meldung an das
 * Portal, die Datenbank mit einer Zusicherung, die auch ein zweiter
 * Schreibweg nicht umgeht.
 */
export const portalEntscheidungEingabeSchema = z
    .object({
    art: portalEntscheidungArtSchema,
    zeitpunkt: zeitpunktEingabe,
    agbFassung: portalZeile(PORTAL_TEXT_MAX).optional(),
    bestellnummer: portalZeile(PORTAL_BESTELLNUMMER_MAX).optional(),
    anmerkung: portalFreitext().optional(),
    grund: portalFreitext().optional(),
    ...herkunft,
})
    .strict()
    .superRefine((wert, ctx) => {
    // `agbFassung` ist hier schon getrimmt (`portalZeile`); `=== ''` genügt.
    if (wert.art === 'angenommen' && (wert.agbFassung === undefined || wert.agbFassung === '')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['agbFassung'],
            message: 'Eine Annahme trägt die Fassung der AGB, unter der sie abgegeben wurde',
        });
    }
});
/** Die Antwort auf eine angenommene oder abgelehnte Entscheidung. */
export const portalEntscheidungAntwortSchema = z
    .object({
    entscheidungId: uuid,
    zeitpunkt,
})
    .strict();
/** `POST /portal/belege/:token/rueckfrage` → `201 { vorgangId }`. */
export const portalRueckfrageSchema = z
    .object({
    text: portalFreitext(PORTAL_TEXT_MAX, 1),
    absenderEmail: portalEmail.optional(),
    ...herkunft,
})
    .strict();
/**
 * Die Felder, deren Änderung ein Kunde beantragen kann — die EINGABE.
 *
 * **Eine geschlossene Liste und kein freier Satz**: Was hier nicht steht,
 * beantragt man mit einer Rückfrage in Prosa. Der Antrag ändert ohnehin
 * nichts selbst — die Sachbearbeitung trägt ihn von Hand in die Kundenmaske
 * (`D-5`, die vier Augen dieser Strecke).
 *
 * **Kein Feld ist leer** (`E-1473`, seit `KP-eingabehaertung`): Bis dahin
 * bestand `{ name: "" }` oder `{ anschrift: [] }` den „mindestens ein
 * Feld"-Check und legte einen Antrag an, der beantragte, den Namen zu löschen.
 * Leer heisst jetzt: das Feld weglassen.
 *
 * | Feld | Regel |
 * |---|---|
 * | `name` | einzeilig, 1–{@link PORTAL_NAME_MAX} Zeichen |
 * | `anschrift` | 1–{@link PORTAL_ANSCHRIFT_ZEILEN_MAX} Zeilen, je einzeilig 1–{@link PORTAL_NAME_MAX} |
 * | `email` | E-Mail, höchstens {@link PORTAL_EMAIL_MAX} |
 * | `telefon` | 1–{@link PORTAL_TELEFON_MAX}, nur Ziffern, Leerzeichen, `+ - / ( )`, mindestens eine Ziffer |
 * | `ustId` | roh bis {@link PORTAL_UST_ID_MAX} mit Trennern (Leerzeichen, Punkt, Bindestrich); gespeichert ohne Trenner, grossgeschrieben, `^[A-Z]{2}[A-Z0-9]{2,13}$` |
 *
 * Alle Längen in Codepunkten; Name und Anschriftszeilen erlauben die Verbinder
 * `U+200C`/`U+200D`, alle anderen Felder nicht.
 *
 * **Das LESESCHEMA der gespeicherten Anträge ist
 * {@link portalAenderungsfelderLeseSchema}, und es bleibt nachsichtig.** Ein
 * Leseschema, das mit dem Eingabeschema strenger würde, verwandelte jeden
 * Antrag, der unter der alten Regel angenommen wurde, in der Arbeitsliste in
 * den Platzhalter „entspricht nicht der erwarteten Form" (`zuVorgang()` in
 * `portal-vorgang.service.ts`) — die Sachbearbeitung verlöre einen echten
 * Antrag, weil die Regel nach ihm schärfer wurde. Streng beim Schreiben,
 * nachsichtig beim Lesen; jede strenge Form ist eine nachsichtige.
 */
export const portalAenderungsfelderSchema = z
    .object({
    name: portalNamenszeile(PORTAL_NAME_MAX, 1).optional(),
    anschrift: z
        .array(portalNamenszeile(PORTAL_NAME_MAX, 1))
        .min(1, 'Eine Anschrift hat mindestens eine Zeile')
        .max(PORTAL_ANSCHRIFT_ZEILEN_MAX)
        .optional(),
    email: portalEmail.optional(),
    telefon: portalTelefon.optional(),
    ustId: portalUstId.optional(),
})
    .strict()
    .refine((wert) => Object.keys(wert).length > 0, {
    message: 'Ein Änderungsantrag ohne ein einziges Feld ist keiner',
});
/**
 * Die Felder eines GESPEICHERTEN Änderungsantrags — das Leseschema
 * (`sql:jsonb`-Konvention), in der Form vor `KP-eingabehaertung`.
 *
 * Nachsichtig mit Absicht, siehe {@link portalAenderungsfelderSchema}: Jeder
 * Wert, den das Eingabeschema heute annimmt, nimmt auch dieses an — die
 * Richtung, in der ein Schreibweg nie eine unlesbare Zeile anlegen kann.
 */
export const portalAenderungsfelderLeseSchema = z
    .object({
    name: z.string().max(PORTAL_TEXT_MAX).optional(),
    anschrift: z.array(z.string().max(PORTAL_TEXT_MAX)).optional(),
    email: z.string().email().optional(),
    telefon: z.string().max(PORTAL_TEXT_MAX).optional(),
    ustId: z.string().max(PORTAL_TEXT_MAX).optional(),
})
    .strict()
    .refine((wert) => Object.keys(wert).length > 0, {
    message: 'Ein Änderungsantrag ohne ein einziges Feld ist keiner',
});
/** `POST /portal/kunden/:kundeId/aenderungsantrag` → `201 { vorgangId }`. */
export const portalAenderungsantragSchema = z
    .object({
    felder: portalAenderungsfelderSchema,
    anmerkung: portalFreitext().optional(),
    ...herkunft,
})
    .strict();
/** Die Antwort beider Vorgangsrouten. */
export const portalVorgangAntwortSchema = z.object({ vorgangId: uuid }).strict();
// ---------------------------------------------------------------------------
// Altlink und Kennung
// ---------------------------------------------------------------------------
/**
 * `POST /portal/altlinks/aufloesen`.
 *
 * **Das Feld heisst `hash` und nicht `h`**, obwohl der Altlink den Buchstaben
 * trägt — und daran hängt eine Zusicherung: `redactBody()`
 * (`apps/api/src/audit/access-log-body.ts`) schwärzt nach TEILZEICHENKETTEN
 * im Feldnamen, und `hash` steht auf seiner Liste. Ein Feld namens `h` stünde
 * im Rumpf jeder `404` in `access_log_body` (`E-1385`).
 */
export const portalAltlinkSchema = z
    .object({
    nummer: portalZeile(PORTAL_NUMMER_MAX, 1),
    hash: portalZeile(PORTAL_HASH_MAX, 1),
})
    .strict();
/** Die Antwort der Altlink-Auflösung — mehr als den Token gibt es nicht. */
export const portalAltlinkAntwortSchema = z.object({ token: z.string() }).strict();
/** `POST /portal/kennungen/aufloesen` — E-Mail oder Anzeige-Kundennummer (`D-10`). */
export const portalKennungSchema = z
    .object({ kennung: portalZeile(PORTAL_KENNUNG_MAX, PORTAL_KENNUNG_MIN) })
    .strict();
/** Ein Konto, das zu einer Kennung gehört. */
export const portalKennungKundeSchema = z
    .object({
    kundeId: uuid,
    kundennummer: z.string(),
    name: z.string(),
})
    .strict();
/**
 * Die Antwort der Kennungsauflösung.
 *
 * **`kunden` ist eine Liste, und das ist kein Vorratsbeschluss** (`D-10`):
 * `customer.email` ist nicht eindeutig, und die Dublettenprüfung dieses
 * Systems markiert nur, sie führt nicht zusammen (`customer_link.status`).
 * Dieselbe Person kann zwei aktive Konten tragen; das Portal zeigt dann einen
 * Kontowechsler (`E-1404`).
 *
 * **`email` ist vollständig und nicht maskiert**, weil das Portal den
 * Einmalcode dorthin schickt. Nur das Portal sieht diese Antwort; dem Browser
 * gegenüber antwortet es in beiden Fällen gleich.
 */
export const portalKennungAntwortSchema = z
    .object({
    email: z.string(),
    kunden: z.array(portalKennungKundeSchema),
})
    .strict();
// ---------------------------------------------------------------------------
// Das Kundenkonto
// ---------------------------------------------------------------------------
/**
 * Das aktive SEPA-Mandat am Konto — **ohne Gläubiger-ID** (`E-1405`).
 *
 * Sie steht am Ausstellerprofil (`issuer_profile.sepa_glaeubiger_id`), und
 * ein Kundenkonto hat kein Profil. Der Kunde sieht sie an jedem Beleg mit
 * `lastschrift`, also genau dort, wo sie hingehört.
 */
export const portalSepaMandatSchema = z
    .object({
    mandatsreferenz: z.string(),
    /** Die letzten vier Stellen, alles davor maskiert. */
    ibanMaskiert: z.string(),
    /** `mandatsdatum` — der Tag der Unterschrift. */
    seit: isoDate,
})
    .strict();
/** `GET /portal/kunden/:kundeId` — die AKTUELLEN Stammdaten, nicht der Belegstand. */
export const portalKundeSchema = z
    .object({
    kundennummer: z.string(),
    name: z.string(),
    kontakt: z.string().optional(),
    anschrift,
    email: z.string(),
    telefon: z.string().optional(),
    ustId: z.string().optional(),
    sepaMandat: portalSepaMandatSchema.optional(),
    /**
     * Der HEUTE gültige Rechnungssteller (Auftrag `KP-leistungszeitraum`) —
     * dieselbe Form wie `PortalBeleg.aussteller`, aber **nicht** die Fassung
     * eines Belegs: Dieser Block steht auf der Kontoseite („Fragen zu einer
     * Rechnung?") unabhängig von einem einzelnen Beleg, deshalb die Fassung
     * von `issuer_profile` mit `valid_from <= heute AND (valid_to IS NULL OR
     * valid_to > heute)` — dieselbe Auflösung wie bei jeder anderen
     * versionierten Stammdatentabelle dieses Hauses (`account`,
     * `bank_account`, …).
     *
     * Fehlt, wenn es zum heutigen Tag KEINE gültige Fassung gibt (Datenlücke
     * zwischen zwei Fassungen) — kein Fehler, dasselbe Muster wie
     * `sepaMandat` oben.
     */
    aussteller: portalAusstellerSchema.optional(),
})
    .strict();
/** Eine Zeile der Belegliste eines Kontos (Spec 6.4). */
export const portalKundenBelegZeileSchema = z
    .object({
    token: z.string(),
    art: portalBelegArtSchema,
    nummer: z.string(),
    datum: isoDate,
    faelligAm: isoDate.optional(),
    gueltigBis: isoDate.optional(),
    brutto: centAmount,
    offen: centAmount,
    status: portalBelegStatusSchema,
})
    .strict();
/**
 * `GET /portal/kunden/:kundeId/belege`.
 *
 * **`jahre` und `summen` sind UNGEFILTERT** (`E-1407`): Sie beantworten „was
 * gibt es überhaupt" und „wie viel schulde ich insgesamt". Eine Jahresliste,
 * die nur die Jahre des gerade gewählten Jahres kennt, wäre ein Menü, das
 * sich selbst zuklappt.
 *
 * **Kein Seitenumbruch** (`D-11`): Der grösste Kunde hat gemessen unter 400
 * Belege.
 */
export const portalKundenBelegListeSchema = z
    .object({
    zeilen: z.array(portalKundenBelegZeileSchema),
    jahre: z.array(z.number().int()),
    summen: z
        .object({
        offen: centAmount,
        anzahlOffen: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------------------
// `portal_vorgang.inhalt` — die Leseschemas (`sql:jsonb`-Konvention)
// ---------------------------------------------------------------------------
/**
 * Der Inhalt einer Rückfrage.
 *
 * **Leseschema nach der `sql:jsonb`-Konvention**: Der Dienst liest die Spalte
 * über `portalVorgangInhaltSchema.safeParse(zeile.inhalt)`, statt dem
 * gespeicherten Wert blind zu vertrauen. Die Datenbank prüft nur die obersten
 * Schlüsselnamen (`portal_vorgang_inhalt_schluessel`, `0236`) — dass eine
 * Spalte mit einem Schlüssel-CHECK trotzdem eine Form durchlässt, die dieses
 * Schema ablehnt, ist genau die Fehlerklasse aus Handbuch 3.420, und deshalb
 * gibt es beides.
 */
export const portalRueckfrageInhaltSchema = z
    .object({
    text: z.string().min(1).max(PORTAL_TEXT_MAX),
    absenderEmail: z.string().email().optional(),
})
    .strict();
/** Der Inhalt eines Änderungsantrags. */
export const portalAenderungsantragInhaltSchema = z
    .object({
    felder: portalAenderungsfelderLeseSchema,
    anmerkung: z.string().max(PORTAL_TEXT_MAX).optional(),
})
    .strict();
/**
 * `portal_vorgang.inhalt`, je Art.
 *
 * Eine Vereinigung und keine unterscheidende Vereinigung
 * (`discriminatedUnion`): Die Art steht in einer EIGENEN Spalte und nicht im
 * JSON — ein Unterscheidungsmerkmal im Inhalt wäre dieselbe Angabe zum
 * zweiten Mal, und zwei Angaben derselben Tatsache können auseinanderlaufen.
 * Wer die Art kennt, nimmt {@link portalVorgangInhaltSchemaFuer}.
 */
export const portalVorgangInhaltSchema = z.union([
    portalRueckfrageInhaltSchema,
    portalAenderungsantragInhaltSchema,
]);
/** Das engere Leseschema, wenn die Art bekannt ist. */
export function portalVorgangInhaltSchemaFuer(art) {
    return art === 'rueckfrage' ? portalRueckfrageInhaltSchema : portalAenderungsantragInhaltSchema;
}
// ---------------------------------------------------------------------------
// Die Betriebsliste — `GET /api/v1/portal-vorgaenge`
// ---------------------------------------------------------------------------
/**
 * Ein Portal-Vorgang, wie die Sachbearbeitung ihn sieht.
 *
 * `nullable` statt `optional`, anders als im Portal-Beleg oben: Ein nicht
 * erledigter Vorgang kennt die Frage „wann erledigt" sehr wohl, und die
 * Antwort ist „noch nicht". Ein fehlendes Feld hiesse dagegen „diese Fassung
 * kennt die Frage nicht".
 *
 * **Ohne `ip` und `userAgent`**, wie die Sicht `portal_vorgang_offen`: Eine
 * Arbeitsliste beantwortet, was zu tun ist. Wer die Herkunft braucht, liest
 * die Zeile — und die Löschfrist von 90 Tagen (`D-22`) gilt für sie.
 *
 * **`absenderEmailStimmt` und `kundenEmail` — Sicherheitsreview
 * `KP-eingaben`, 2026-09-25, `E-1480`.** Eine Rückfrage trägt in `inhalt`
 * eine vom KUNDEN GETIPPTE Absenderadresse, unbestätigt — wer einen
 * weitergeleiteten Beleglink hat, kann sie auf eine fremde Adresse setzen.
 * Der Vergleich mit der hinterlegten Adresse des Kontos gehört deshalb in
 * den DIENST und nicht in die Anzeige: Das Web soll nicht selbst
 * nachrechnen, ob zwei Zeichenketten „gleich genug" sind.
 * `absenderEmailStimmt` ist `null`, wenn kein Vergleich möglich ist — keine
 * `absenderEmail` in `inhalt` (ein Änderungsantrag kennt das Feld gar
 * nicht), oder das Konto führt keine E-Mail-Adresse. `kundenEmail` ist die
 * hinterlegte Adresse selbst, damit eine Abweichung sich an der Zeile
 * selbst auflösen lässt und nicht erst über die Kundenseite — dieselbe
 * Kontoadresse, die dort ohnehin steht, keine zusätzliche Herkunftsangabe
 * wie `ip`/`userAgent`.
 */
export const portalVorgangSchema = z
    .object({
    id: uuid,
    art: portalVorgangArtSchema,
    erstelltAm: zeitpunkt,
    kundeId: uuid,
    kundennummer: z.string(),
    kundenname: z.string(),
    kundenEmail: z.string().nullable(),
    belegId: uuid.nullable(),
    belegnummer: z.string().nullable(),
    inhalt: portalVorgangInhaltSchema,
    absenderEmailStimmt: z.boolean().nullable(),
    erledigtAm: z.string().nullable(),
    erledigtVon: uuid.nullable(),
    erledigtNotiz: z.string().nullable(),
})
    .strict();
/**
 * Der Filter der Arbeitsliste — `GET /api/v1/portal-vorgaenge?status=…`.
 *
 * Zwei Werte und kein dritter: Ein Vorgang ist erledigt oder er ist es nicht,
 * und `erledigt_am` sagt das allein (Migration `0236`). Ein `alle` gäbe es
 * dazu, und die Seite müsste je Zeile entscheiden, in welche Spalte sie
 * gehört — die Arbeitsliste ist aber genau die Frage «was liegt noch da».
 */
export const portalVorgangFilterSchema = z.enum(['offen', 'erledigt']);
/**
 * Die Abfrage der Arbeitsliste — `GET /api/v1/portal-vorgaenge?status=…&kundeId=…&limit=…&offset=…`
 * (`KP-mengengrenzen`, `D-28`).
 *
 * `limit` hat die Vorgabe {@link PORTAL_VORGAENGE_SEITE_VORGABE} und höchstens
 * {@link PORTAL_VORGAENGE_SEITE_MAX}; darüber ist die Anfrage `400` und wird
 * nicht still gekürzt — eine gekürzte Liste sähe aus wie eine vollständige.
 * `kundeId` grenzt auf die Vorgänge EINES Kunden ein; die Kundenakte fragt
 * damit nur nach ihren eigenen, statt die ganze Liste zu laden und zu filtern.
 */
export const portalVorgangAbfrageSchema = z
    .object({
    status: portalVorgangFilterSchema,
    kundeId: uuid.optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(PORTAL_VORGAENGE_SEITE_MAX)
        .default(PORTAL_VORGAENGE_SEITE_VORGABE),
    offset: z.coerce.number().int().min(0).default(0),
})
    .strict();
/**
 * `GET /api/v1/portal-vorgaenge` — der Umschlag der Arbeitsliste.
 *
 * Ein benannter Umschlag im Vertrag und keiner, den die Oberfläche sich aus
 * dem Item-Schema selbst zusammensetzt: `pnpm listen:vertrag` hält jede
 * Listenroute gegen genau den Umschlag, den `apps/web/src/api/client.ts`
 * beim echten Aufruf benutzt (`E-1415`/`E-1126`), und zwei Zusammensetzungen
 * derselben Liste — eine im Test, eine in der Oberfläche — wären zwei
 * Wahrheiten über dieselbe Antwort.
 *
 * **Mit Seitenumbruch seit `KP-mengengrenzen` (2026-09-26, `D-28`).** Bis
 * dahin stand hier „kein Seitenumbruch, die offenen Vorgänge eines Hauses mit
 * zwanzig Verträgen sind eine Handvoll". Das galt für den gewollten Gebrauch;
 * gegen einen gestohlenen Bearer, der Vorgänge in beliebiger Zahl anlegt,
 * wäre die Liste die Stelle, an der die Oberfläche erstickt. `gesamt` nennt die
 * Zahl ohne Seite — das Abzeichen der Menüspalte liest sie, statt eine Seite
 * zu zählen. Wer in den erledigten sucht, sucht über den Kunden (`kundeId`).
 */
export const portalVorgangListeSchema = z
    .object({
    vorgaenge: z.array(portalVorgangSchema),
    /** Wie viele Vorgänge die Abfrage ohne `limit`/`offset` träfe (`D-28`). */
    gesamt: z.number().int().nonnegative(),
})
    .strict();
/**
 * `POST /api/v1/portal-vorgaenge/:id/erledigen` — die Notiz ist freiwillig.
 *
 * **Ein leerer Rumpf ist gültig**, und das ist die Entscheidung: Die meisten
 * Vorgänge sind mit einem Anruf erledigt, und eine Pflichtnotiz erzwänge
 * einen Satz wie «erledigt», der nichts sagt. Wer etwas zu sagen hat, sagt es;
 * die Länge hält `PORTAL_TEXT_MAX` und dieselbe Zahl als CHECK
 * (`portal_vorgang_notiz_laenge`, `0236`).
 */
export const portalVorgangErledigenSchema = z
    .object({ notiz: portalFreitext().optional() })
    .strict();
//# sourceMappingURL=portal.js.map